"use client"

import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { CountryCode } from "@/data/country-data"
import {
    getUserCountryOriginClient,
    getUserLocaleClient,
    setUserLocaleClient
} from "@/lib/cookie/site-cookie/cookieActions.client"
import {
    getCountryAdjectiveFromCode,
    mapCountryCodeToCountryName
} from "@/maps/map-utils"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"

const TITLE = "Hi there!"
const BUTTON_TEXT = "choose market & language"

export default function WrongSitePopupModal({ locale }: { locale: string }) {
    const [userCountry, setUserCountry] = useState<CountryCode | null>(null)
    const [hasSeenWrongSitePopup, setHasSeenWrongSitePopup] = useState<boolean>(false)
    const setShowCountryPicker = useEcommerceStore(
        ({ setShowCountryPicker }) => setShowCountryPicker
    )
    const showCountryPicker = useEcommerceStore(
        ({ showCountryPicker }) => showCountryPicker
    )
    const centraSessionCountryCode = useEcommerceStore(
        state => state.centraSession.metadata.country.code
    )

    const handleCloseWrongSitePopup = async (toastId: string) => {
        toast.dismiss(toastId)
        setUserLocaleClient({
            hasSeenWrongSitePopup: true,
            path: `/${locale}`
        })
    }

    useEffect(() => {
        // Dev mode - disable in env
        if (process.env.NEXT_PUBLIC_DISABLE_WRONG_SITE_POPUP === "true") {
            return
        }
        const userLocaleCookie = getUserLocaleClient()
        if (userLocaleCookie.hasSeenWrongSitePopup === true) {
            setHasSeenWrongSitePopup(true)
            toast.dismiss("wrong-site-popup")
        }
        const origin = getUserCountryOriginClient()

        if (origin) {
            setUserCountry(origin)
        }

        if (
            window !== undefined &&
            userCountry &&
            centraSessionCountryCode &&
            !hasSeenWrongSitePopup &&
            centraSessionCountryCode !== userCountry
        ) {
            const userCountryName = mapCountryCodeToCountryName(userCountry)
            const visitingCountryName = mapCountryCodeToCountryName(
                centraSessionCountryCode
            )

            const suggestedAdjective = getCountryAdjectiveFromCode(userCountry)

            toast.custom(
                t => (
                    <div
                        className={`${
                            t.visible
                                ? "animate-slide-in-bottom md:animate-slide-in-right"
                                : "animate-slide-out-bottom md:animate-slide-out-right"
                        } fixed bottom-[78px] right-auto max-w-sm rounded-lg bg-black px-16 py-24 text-white shadow-lg md:bottom-auto md:right-0 md:top-[90px] md:p-24`}
                    >
                        <div className="mb-24 flex items-center justify-between">
                            <p className="font_trimMonoPoc_Regular_14_120_uppercase">
                                {TITLE}
                            </p>
                            <button onClick={() => handleCloseWrongSitePopup(t.id)}>
                                <IconClose aria-label="Close wrong site popup" />
                            </button>
                        </div>
                        <p className="mb-24 font_tradeGothicLTPro_Light_16_130">
                            {`It looks like you're visiting from ${userCountryName}, but you're about to enter the ${visitingCountryName} website. Would you like to switch to our ${suggestedAdjective} site instead?`}
                        </p>
                        <ButtonPill
                            color="white"
                            size="medium"
                            variant="tertiary"
                            className="font_trimMonoPoc_Regular_14_120_uppercase"
                            onClick={() => {
                                handleCloseWrongSitePopup(t.id)
                                !showCountryPicker && setShowCountryPicker(true)
                            }}
                        >
                            {BUTTON_TEXT}
                            <IconArrowRight
                                aria-label="Open market & language modal"
                                className="h-12 w-12"
                            />
                        </ButtonPill>
                    </div>
                ),
                {
                    id: "wrong-site-popup",
                    duration: Infinity,
                    position: window.innerWidth < 968 ? "bottom-center" : "top-right"
                }
            )
        }
        return () => {
            toast.dismiss("wrong-site-popup")
        }
    }, [centraSessionCountryCode, userCountry])

    return null
}
